'use client';

import { useSyncExternalStore } from 'react';
import { ANALYSIS_ENGINES, engineGrantMissing, type AnalysisEngine, type HostedEngineGrants } from '@/types/optical';
import { writeSyncedLocalStorage } from '@/lib/syncedLocalStorage';

const ANALYSIS_SERVICE_KEY = 'photonium_analysis_service';
const ANALYSIS_SERVICE_CHANGED_EVENT = 'analysisServiceChanged';
// Prefix of the per-conversation key holding a read-only viewer's engine pick
// for someone else's conversation (see readViewerEngine).
const VIEWER_ENGINE_KEY_PREFIX = 'photonium_viewer_engine:';

const VALID_SERVICES: ReadonlySet<AnalysisEngine> = new Set(ANALYSIS_ENGINES);

function readValidEngine(key: string): AnalysisEngine | null {
  const v = localStorage.getItem(key);
  return v && VALID_SERVICES.has(v as AnalysisEngine) ? (v as AnalysisEngine) : null;
}

function readStored(): AnalysisEngine {
  if (typeof window === 'undefined') return 'optiland';
  return readValidEngine(ANALYSIS_SERVICE_KEY) ?? 'optiland';
}

/** The engine a read-only viewer picked for someone else's conversation, or
 *  null when they never switched. A viewer has no write path to the owner's
 *  stamped engine, so their pick lives in this browser, keyed by conversation
 *  id, and outlives a reload; the stamped engine governs until they switch. */
export function readViewerEngine(conversationId: string): AnalysisEngine | null {
  return readValidEngine(VIEWER_ENGINE_KEY_PREFIX + conversationId);
}

export function writeViewerEngine(conversationId: string, engine: AnalysisEngine): void {
  localStorage.setItem(VIEWER_ENGINE_KEY_PREFIX + conversationId, engine);
}

// Stamped engine of the conversation open in THIS tab (null = draft chat or a
// non-conversation page). Module-level rather than React state so the
// imperative getSelectedService() — which every API helper uses to attach the
// engine to requests — resolves to the conversation's engine too, not just the
// hook consumers. Per-tab by construction, so two tabs on different
// conversations each see their own engine.
let conversationEngine: AnalysisEngine | null = null;

// Which hosted pools the signed-in user may use (admin OR the per-user
// allow_* grant). Folded into the effective engine below so a non-granted
// user never sees a hosted view — a shared conversation stamped with a hosted
// engine, or a stale stored default after a revoked grant, falls back to
// optiland for the tabs AND for every request helper reading
// getSelectedService() (viewers dispatch live analyses on mount, and the
// backend 403s each one). Assume granted until the session resolves — a
// session's explicit `false` reliably means no access.
let hostedGrants: HostedEngineGrants = { canUseHostedZemax: true, canUseHostedCodev: true };

/** Fold the session's hosted-pool grants into the effective engine. The page
 *  calls this from its session effect; every consumer re-renders via the same
 *  change event the localStorage default uses. */
export function setHostedGrants(grants: HostedEngineGrants): void {
  if (hostedGrants.canUseHostedZemax === grants.canUseHostedZemax && hostedGrants.canUseHostedCodev === grants.canUseHostedCodev) return;
  hostedGrants = { ...grants };
  window.dispatchEvent(new Event(ANALYSIS_SERVICE_CHANGED_EVENT));
}

/** Set (or clear with null) the open conversation's stamped engine. The
 *  conversation page calls this from its conversation fetch; every consumer
 *  re-renders via the same change event the localStorage default uses. */
export function setConversationEngine(engine: AnalysisEngine | null): void {
  if (conversationEngine === engine) return;
  conversationEngine = engine;
  window.dispatchEvent(new Event(ANALYSIS_SERVICE_CHANGED_EVENT));
}

/** The current tab-level override itself (null = none) — lets the engine
 *  picker's failure path revert to the true prior override rather than
 *  freezing the browser default in as a fake stamped engine. */
export function getConversationEngine(): AnalysisEngine | null {
  return conversationEngine;
}

function readEffective(): AnalysisEngine {
  const engine = conversationEngine ?? readStored();
  return engineGrantMissing(engine, hostedGrants) ? 'optiland' : engine;
}

function subscribe(onChange: () => void): () => void {
  const onStorageEvent = (e: StorageEvent): void => {
    if (e.key === ANALYSIS_SERVICE_KEY || e.key === null) onChange();
  };
  window.addEventListener(ANALYSIS_SERVICE_CHANGED_EVENT, onChange);
  window.addEventListener('storage', onStorageEvent);
  return () => {
    window.removeEventListener(ANALYSIS_SERVICE_CHANGED_EVENT, onChange);
    window.removeEventListener('storage', onStorageEvent);
  };
}

/** Effective engine: the open conversation's stamped engine when one is set,
 *  else the browser-wide new-chat default. `setService` writes only the
 *  default — a stamped conversation's engine changes through
 *  PATCH /conversations/:id/settings + setConversationEngine. */
export function useAnalysisService() {
  const service = useSyncExternalStore(subscribe, readEffective, readEffective);

  function setService(s: AnalysisEngine): void {
    writeSyncedLocalStorage(ANALYSIS_SERVICE_KEY, ANALYSIS_SERVICE_CHANGED_EVENT, s);
  }

  return { service, setService };
}

export function getSelectedService(): AnalysisEngine {
  return readEffective();
}

/** The browser-wide new-chat default alone, ignoring any open conversation's
 *  stamped engine — for code that manages the default itself (e.g. resetting a
 *  revoked hosted default without touching the conversation). */
export function getStoredEngineDefault(): AnalysisEngine {
  return readStored();
}

/** First-visit default for users granted a hosted pool: if no engine choice
 *  has been stored yet, materialize that pool — Zemax when granted, else
 *  CODE V when granted, else nothing — so every consumer (hook and
 *  getSelectedService alike) sees it. A stored value — including a deliberate
 *  'optiland' pick — is never overwritten. */
export function defaultToHostedEngine(grants: HostedEngineGrants): void {
  if (typeof window === 'undefined') return;
  if (readValidEngine(ANALYSIS_SERVICE_KEY)) return;
  const engine = grants.canUseHostedZemax ? 'zemax' : grants.canUseHostedCodev ? 'codev' : null;
  if (engine === null) return;
  writeSyncedLocalStorage(ANALYSIS_SERVICE_KEY, ANALYSIS_SERVICE_CHANGED_EVENT, engine);
}
